import { useEffect, useMemo } from 'react';

import { FormikProps } from 'formik';
import { useNavigate } from 'react-router-dom';

import {
  adminActions,
  adminBroadcastGroupsForDropdown,
  adminResetValue,
  BroadcastNotificationType,
  getBroadcastRecipients,
  resetStatus,
  successSelector,
} from '@pharmaplan/common';
import { showSuccess } from '../../../components/Admin/Profile/Pharmacist/PharmacistViewProfile/helper';

import strings from '../../../localization';

import { useAppSelector } from '../../useAppSelector';
import { useAppDispatch } from '../../useAppDispatch';

const { message, email, pushNotification, chat } = BroadcastNotificationType;
const { sendBroadcastNotification: successAction } = adminActions;
const recipientKey = 'groupRecipientList';

/*
iOS App Push: 150-178 characters for the combined title and body (roughly 4 lines of visible text).
Android App Push: 65 characters for titles, and up to 240 characters for the body.
Web Push (Chrome/Firefox/Safari): Typically 40-60 characters for titles and 150-250 characters for descriptions (depending heavily on the device OS)
*/

const useBroadcastNotification = (
  type: BroadcastNotificationType,
  formik: FormikProps<any>,
) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const success = useAppSelector((state) =>
    successSelector([successAction], state));

  const { groupIds } = formik.values;
  const broadcastGroups = useAppSelector(adminBroadcastGroupsForDropdown);
  const { data } = broadcastGroups;
  const groupsDropdown = useMemo(
    () =>
      data.map((item) =>
        ({
          key: item.broadcastGroupId,
          label: item.name,
        })),

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [broadcastGroups],
  );

  const fieldsConfig = {
    [message]: {
      placeholder: strings.writeMessageContent,
      body: strings.textMessageBody,
      fieldTitle: strings.enterMessageData,
      maxLength: 320,
    },
    [chat]: {
      placeholder: strings.writeMessageContent,
      body: strings.textMessageBody,
      fieldTitle: strings.enterMessageData,
      maxLength: 320,
    },
    [pushNotification]: {
      placeholder: strings.writeMessageContent,
      body: strings.textMessageBody,
      fieldTitle: strings.enterMessageData,
      maxLength: 180,
    },
    [email]: {
      placeholder: strings.writeEmailContent,
      body: strings.emailBody,
      fieldTitle: strings.enterEmailData,
      maxLength: null,
    },
  };

  const getGroups = () => {
    if (groupIds.length) {
      dispatch(getBroadcastRecipients(groupIds));
    } else {
      dispatch(adminResetValue([recipientKey]));
    }
  };

  const { body, placeholder, fieldTitle, maxLength } = fieldsConfig[type as keyof typeof fieldsConfig] ?? {};

  useEffect(() => {
    if (success) {
      showSuccess(dispatch, strings.sentSuccessfully);
      dispatch(resetStatus([successAction]));
      navigate(-1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  useEffect(
    () =>
      () => {
        dispatch(adminResetValue([recipientKey]));
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return {
    body,
    placeholder,
    groupsDropdown,
    getGroups,
    fieldTitle,
    maxLength,
  };
};

export default useBroadcastNotification;
